import { Link } from 'react-router';

import { buttonStyles } from '@psspl/ui';

import { CompanyLogo } from '../brand/company-logo';
import { ROUTES } from '../../lib/routes';
import type { RouteErrorDetail } from '../../types';

export interface StatusPageProps {
  readonly detail: RouteErrorDetail;
  /** e.g. "403" rendered as a large mono numeral. */
  readonly code: string;
}

/**
 * Shared layout for 403 / 404.
 *
 * Deliberately not a `<Navigate>`: a forbidden or missing URL has to be
 * *addressable* so the user can bookmark it, share it and go back. The shell's route
 * transition already animates entry, so this adds no motion of its own.
 */
export function StatusPage({ detail, code }: StatusPageProps) {
  return (
    <div className="flex min-h-[60dvh] items-center justify-center">
      <div className="flex w-full max-w-md flex-col items-center gap-5 rounded-lg border border-line-subtle bg-surface-raised px-6 py-8 text-center">
        <CompanyLogo size="md" />

        <p className="font-mono text-4xl font-semibold tracking-tight text-brand-300">{code}</p>

        <div className="flex flex-col gap-2">
          <h1 className="text-xl font-semibold tracking-tight text-ink-primary">{detail.title}</h1>
          <p className="text-sm leading-relaxed text-ink-secondary">{detail.message}</p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2">
          <Link to={ROUTES.projects} className={buttonStyles()}>
            Go to projects
          </Link>
          <Link to={ROUTES.profile} className={buttonStyles({ variant: 'secondary' })}>
            View profile
          </Link>
        </div>
      </div>
    </div>
  );
}

export const FORBIDDEN_DETAIL: RouteErrorDetail = {
  title: 'You do not have access to this area',
  message:
    'This page is restricted to administrators. If you believe you should have access, contact your portal administrator.',
};

export const NOT_FOUND_DETAIL: RouteErrorDetail = {
  title: 'That page does not exist',
  message: 'The link may be out of date, or the page may have moved.',
};
